import { Button } from "@shared-ui/button";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { Textarea } from "@shared-ui/textarea";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { listAdminBrands } from "@/features/catalog/services/catalog-api.service";
import {
	type CreateSourceInput,
	createImportSource,
	type ImportSource,
	type UpdateSourceInput,
	updateImportSource,
} from "@/features/catalog/services/import-api.service";
import { apiErrorMessage } from "@/lib/api-error";
import { EXTRACTOR_OPTIONS } from "../lib/extractor-options.js";

/**
 * Register a source, or correct one.
 *
 * ONE FORM FOR BOTH. A source is six fields, and the thing an operator most
 * often needs after registering one is to fix the cadence or the extractor they
 * chose wrong -- so a create-only screen leaves `PATCH /sources/:id` with no
 * operator path at all, which is the gap this whole plan is closing.
 *
 * A dialog rather than a page because the LIST is the context: which sources
 * already exist is what tells an operator whether they are registering a new
 * one or duplicating something under a slightly different name.
 */

const TITLE_ID = "source-dialog-title";

/**
 * What a source publishes, and what basis its numbers are on.
 *
 * Both mirror pgEnums the server derives its schema from, so an unknown value
 * here is refused by name rather than stored. Only the shapes an operator
 * plausibly registers are offered: `priceBasisEnum` has fourteen members, most
 * of which describe what a document turned out to be rather than what someone
 * would choose up front.
 *
 * A stored value outside the offered set therefore renders an empty Select on
 * edit. `mrp_basis_unstated` is offered for that reason: the source registry
 * seeder writes it as a placeholder, and a registry row can state it outright.
 * Deriving both lists from the server would close the gap for good.
 */
const KIND_OPTIONS = [
	{ value: "brand_pricelist", label: "Brand price list" },
	{ value: "brand_website", label: "Brand website" },
	{ value: "dealer_hosted", label: "Dealer-hosted list" },
	{ value: "marketplace", label: "Marketplace" },
	{ value: "vendor_feed", label: "Vendor feed" },
	{ value: "dsr_schedule", label: "DSR schedule" },
];

/**
 * Exported so the commit confirmation can name a basis in the same words the
 * dialog that SET it used. Two lists would let the run say "MRP" where the
 * source says something else, on the one screen whose job is to catch that.
 */
export const BASIS_OPTIONS = [
	{ value: "mrp", label: "MRP" },
	{ value: "mrp_incl_gst", label: "MRP including GST" },
	{ value: "mrp_basis_unstated", label: "Basis unstated or contradictory" },
	{ value: "dealer_net", label: "Dealer net" },
	{ value: "distributor_net", label: "Distributor net" },
	{ value: "list_price_ex_gst", label: "List price, excluding GST" },
	{ value: "ex_factory", label: "Ex-factory" },
];

export type SourceDialogMode =
	| { kind: "create" }
	| { kind: "edit"; source: ImportSource };

interface SourceDialogProps {
	mode: SourceDialogMode;
	onClose: () => void;
	onSaved: () => void;
}

export function SourceDialog({ mode, onClose, onSaved }: SourceDialogProps) {
	const editing = mode.kind === "edit" ? mode.source : null;

	const [name, setName] = useState(editing?.name ?? "");
	const [brandId, setBrandId] = useState(editing?.brandId ?? "");
	const [kind, setKind] = useState(editing?.kind ?? "brand_pricelist");
	const [defaultBasis, setDefaultBasis] = useState(
		editing?.defaultBasis ?? "mrp",
	);
	const [cadence, setCadence] = useState(
		String(editing?.refreshCadenceDays ?? 30),
	);
	// A seeded source carries a null key, which means the CSV reader. Showing it
	// as "csv" is what that null means rather than a blank the operator has to
	// interpret; saving it back writes the explicit key, which behaves the same.
	const [parserKey, setParserKey] = useState(editing?.parserKey ?? "csv");
	const [url, setUrl] = useState(editing?.url ?? "");
	const [notes, setNotes] = useState(editing?.notes ?? "");

	const brands = useQuery({
		queryKey: ["admin", "catalog", "brands", "for-source"],
		queryFn: listAdminBrands,
	});

	/*
	 * MERGED BRANDS ARE NOT OFFERED.
	 *
	 * The admin brand list deliberately includes them -- that is how a merge
	 * stays visible -- but a source filed under one can be staged against and
	 * never committed, because `createProduct` refuses a superseded brand. The
	 * server refuses this too and names the surviving brand; keeping them out of
	 * the picker means an operator does not have to read that refusal to get it
	 * right.
	 */
	// `!brand.mergedIntoId` rather than `=== null`: the field is optional on the
	// shared type because public brand reads omit it entirely, and an absent
	// value means "not merged" exactly as a null does.
	const options = (brands.data ?? []).filter((brand) => !brand.mergedIntoId);

	const save = useMutation({
		mutationFn: async () => {
			const days = Number.parseInt(cadence, 10);

			if (mode.kind === "create") {
				const input: CreateSourceInput = {
					name: name.trim(),
					brandId,
					kind,
					defaultBasis,
					refreshCadenceDays: days,
					parserKey,
					...(url.trim() ? { url: url.trim() } : {}),
					...(notes.trim() ? { notes: notes.trim() } : {}),
				};
				await createImportSource(input);
				return;
			}

			/*
			 * Only what MOVED. The audit trail records a source edit as the fields
			 * that changed, and a PATCH resending all eight would report a cadence
			 * fix as a rewrite of the whole row.
			 */
			const before = mode.source;
			const patch: UpdateSourceInput = {};
			if (name.trim() !== before.name) patch.name = name.trim();
			if (brandId !== before.brandId) patch.brandId = brandId;
			if (kind !== before.kind) patch.kind = kind;
			if (defaultBasis !== before.defaultBasis) {
				patch.defaultBasis = defaultBasis;
			}
			if (Number.isFinite(days) && days !== before.refreshCadenceDays) {
				patch.refreshCadenceDays = days;
			}
			// `null` and `"csv"` are the same extractor, so switching between them
			// is not a change worth an audit row.
			if (parserKey !== (before.parserKey ?? "csv")) {
				patch.parserKey = parserKey;
			}
			if (url.trim() !== (before.url ?? "")) {
				patch.url = url.trim() === "" ? null : url.trim();
			}
			if (notes.trim() !== (before.notes ?? "")) {
				patch.notes = notes.trim() === "" ? null : notes.trim();
			}

			if (Object.keys(patch).length === 0) return;
			await updateImportSource(before.id, patch);
		},
		onSuccess: () => {
			onSaved();
			onClose();
		},
	});

	const days = Number.parseInt(cadence, 10);
	const canSave =
		name.trim() !== "" &&
		brandId !== "" &&
		Number.isFinite(days) &&
		days >= 1 &&
		days <= 365 &&
		!save.isPending;

	const chosenExtractor = EXTRACTOR_OPTIONS.find(
		(one) => one.value === parserKey,
	);

	return (
		<Dialog
			open
			titleId={TITLE_ID}
			onClose={() => {
				if (!save.isPending) onClose();
			}}
			className="grid max-h-[90vh] w-full max-w-lg gap-4 overflow-y-auto rounded-3xl border bg-white p-5 shadow-xl"
		>
			<div>
				<h2 id={TITLE_ID} className="text-lg font-semibold">
					{editing ? `Edit ${editing.name}` : "Register a source"}
				</h2>
				<p className="text-sm text-muted-foreground">
					A source is where a price list comes from. Imports are staged against
					one.
				</p>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="source-name">Name</Label>
				<Input
					id="source-name"
					value={name}
					onChange={(event) => setName(event.target.value)}
					placeholder="Kajaria price list (spreadsheet)"
				/>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="source-brand">Brand</Label>
				<Select value={brandId} onValueChange={setBrandId}>
					<SelectTrigger id="source-brand">
						<SelectValue placeholder="Choose a brand" />
					</SelectTrigger>
					<SelectContent>
						<SelectGroup>
							{options.map((brand) => (
								<SelectItem key={brand.id} value={brand.id}>
									{brand.name}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>
				<p className="text-xs text-muted-foreground">
					Imported rows are keyed on this brand's SKUs, so a source without one
					can be staged but never committed.
				</p>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="source-kind">What it is</Label>
				<Select value={kind} onValueChange={setKind}>
					<SelectTrigger id="source-kind">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectGroup>
							{KIND_OPTIONS.map((one) => (
								<SelectItem key={one.value} value={one.value}>
									{one.label}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="source-basis">Default price basis</Label>
				<Select value={defaultBasis} onValueChange={setDefaultBasis}>
					<SelectTrigger id="source-basis">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectGroup>
							{BASIS_OPTIONS.map((one) => (
								<SelectItem key={one.value} value={one.value}>
									{one.label}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>
				<p className="text-xs text-muted-foreground">
					What this source's numbers mean unless a row says otherwise. The same
					figure is a different price as MRP and as dealer net.
				</p>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="source-parser">How its documents are read</Label>
				<Select value={parserKey} onValueChange={setParserKey}>
					<SelectTrigger id="source-parser">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectGroup>
							{EXTRACTOR_OPTIONS.map((one) => (
								<SelectItem key={one.value} value={one.value}>
									{one.label}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>
				{chosenExtractor ? (
					<p className="text-xs text-muted-foreground">
						{chosenExtractor.hint}
					</p>
				) : null}
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="source-cadence">Refresh every (days)</Label>
				<Input
					id="source-cadence"
					type="number"
					min={1}
					max={365}
					value={cadence}
					onChange={(event) => setCadence(event.target.value)}
				/>
				<p className="text-xs text-muted-foreground">
					How often this list is expected to change. It decides when the source
					appears as due; it does not fetch anything on its own.
				</p>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="source-url">Where it comes from (optional)</Label>
				<Input
					id="source-url"
					value={url}
					onChange={(event) => setUrl(event.target.value)}
					placeholder="https://example.com/prices"
				/>
				<p className="text-xs text-muted-foreground">
					The page or file this list is published at, for whoever fetches it
					next. Nothing downloads it automatically today.
				</p>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="source-notes">Notes (optional)</Label>
				<Textarea
					id="source-notes"
					rows={2}
					value={notes}
					onChange={(event) => setNotes(event.target.value)}
				/>
			</div>

			{save.isError ? (
				/*
				 * THE SERVER'S OWN SENTENCE. Every refusal these two endpoints make
				 * names the thing to fix -- the unknown extractor and the keys that
				 * do exist, the surviving brand a merged one was folded into, which
				 * of the five URL rules was broken. A generic "could not save" throws
				 * all of that away and leaves the operator guessing.
				 */
				<p role="alert" className="text-sm text-red-700">
					{apiErrorMessage(save.error, "Could not save this source.")}
				</p>
			) : null}

			<div className="flex justify-end gap-2 border-t pt-3">
				<Button variant="ghost" onClick={onClose} disabled={save.isPending}>
					Cancel
				</Button>
				<Button onClick={() => save.mutate()} disabled={!canSave}>
					{save.isPending ? "Saving..." : "Save"}
				</Button>
			</div>
		</Dialog>
	);
}
